import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, GiftIcon, HelpCircleIcon, QuoteIcon, ShieldIcon, TrophyIcon, XIcon } from 'lucide-react';
import { bonuses, developedTestimonials, faqs, modules, pains, purchaseUrl, testimonials } from '../data/content';
import { Button } from './ui/Button';
const collapseTransition = {
  duration: 0.22,
  ease: [0.23, 1, 0.32, 1] as const
};
function Eyebrow({
  children,
  danger = false
}: {
  children: React.ReactNode;
  danger?: boolean;
}) {
  return <p className={`mb-3 text-center text-[11px] font-extrabold uppercase tracking-[.16em] md:text-sm ${danger ? 'text-red-500' : 'text-primary'}`} data-id="element-17" data-mp="1no478ia3clrt">
      {children}
    </p>;
}
function SectionTitle({
  children
}: {
  children: React.ReactNode;
}) {
  return <h2 className="mx-auto max-w-3xl text-center text-2xl font-black leading-tight text-ink md:text-4xl" data-id="element-18" data-mp="2z2zwey6d65mm">
      {children}
    </h2>;
}
export function Testimonials() {
  return <section className="mx-auto max-w-6xl px-4 py-12 md:py-14" data-id="element-19" data-mp="1bodrvpdke6x9">
      <Eyebrow data-id="element-20" data-mp="16q2gha7mfocx">⭐ 4,9/5 · +200 entrenadores de gimnasio</Eyebrow>
      <SectionTitle data-id="element-21" data-mp="16ohnhp0ygdh6">
        Lo que dicen los que <span className="text-primary" data-id="element-22" data-mp="12o7gc91ffh0q">ya lo están usando</span>
      </SectionTitle>
      <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-23" data-mp="xq5ujgcm93lk">
        {testimonials.map(item => <article key={item.name} className="relative flex flex-col rounded-2xl border border-line bg-white p-6 shadow-sm" data-id="element-24" data-mp="sqj94jmmwh1o">
            <QuoteIcon className="absolute right-5 top-5 h-7 w-7 text-primary/15" data-id="element-25" data-mp="nyrrwzpdbno7" />
            <div className="mb-3 text-sm text-gold" data-id="element-26" data-mp="j1j6mq8xb3iq">⭐⭐⭐⭐⭐</div>
            <p className="mb-5 flex-1 text-base font-semibold italic leading-relaxed text-slate-700" data-id="element-27" data-mp="ezxxnfzif9dt">
              “{item.text}”
            </p>
            <div className="mt-auto flex items-center gap-3 border-t border-line pt-4" data-id="element-28" data-mp="9zjo1ti9frdo">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-primary/20 bg-canvas text-sm font-black text-primary" data-id="element-29" data-mp="51qkz9c97dhm">
                {item.initials}
              </span>
              <div data-id="element-30" data-mp="amgumskf2c0">
                <p className="text-sm font-extrabold text-ink" data-id="element-31" data-mp="3hkudt7j5xudr">{item.name}</p>
                <p className="text-xs text-slate-500" data-id="element-32" data-mp="3hmxdkja4b1vo">📍 {item.place}</p>
              </div>
            </div>
          </article>)}
      </div>
    </section>;
}
export function Pain() {
  return <section className="border-y border-line bg-white px-4 py-12 md:py-14" data-id="element-33" data-mp="e6ggsgbd2hcz">
      <div className="mx-auto max-w-4xl" data-id="element-34" data-mp="j2nly6vxnotv">
        <Eyebrow danger data-id="element-35" data-mp="4ekh50tmmj6n">😤 La realidad</Eyebrow>
        <SectionTitle data-id="element-36" data-mp="4drk4rkgz81p">
          ❓ ¿Cuántos alumnos más vas a{' '}
          <span className="text-primary" data-id="element-37" data-mp="9cm07lr1yii4">programar desde cero?</span>
        </SectionTitle>
        <div className="mt-8 grid gap-3 sm:grid-cols-2" data-id="element-38" data-mp="x2gco27gm51u">
          {pains.map(pain => <div key={pain} className="flex items-start gap-3 rounded-xl border border-line bg-canvas p-4" data-id="element-39" data-mp="11tewhbrnpl9u">
              <XIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-500" data-id="element-40" data-mp="2miixvhnbcxlg" />
              <span className="text-sm font-semibold text-slate-700 md:text-base" data-id="element-41" data-mp="2hjz9zbp5tbqg">{pain}</span>
            </div>)}
        </div>
        <p className="mt-8 text-center text-lg font-bold text-slate-700 md:text-2xl" data-id="element-42" data-mp="2w93w33z2s03u">
          No es falta de experiencia.{' '}
          <span className="font-black text-primary" data-id="element-43" data-mp="23l7vho6dxo1s">Es falta de un SISTEMA.</span>
        </p>
      </div>
    </section>;
}
export function ProductOverview() {
  const [open, setOpen] = useState(0);
  return <section className="mx-auto max-w-4xl px-4 py-12 md:py-14" data-id="element-44" data-mp="2cmfkffn0hcj7">
      <Eyebrow data-id="element-45" data-mp="2cjtefkfrr533">
        <span className="inline-flex items-center gap-2" data-id="element-46" data-mp="28kc8kpwfk5xe">
          <TrophyIcon className="h-4 w-4" data-id="element-47" data-mp="28irdmz67ekz0" /> Qué incluye el sistema
        </span>
      </Eyebrow>
      <SectionTitle data-id="element-48" data-mp="3nzi0m9l7802u">
        6 tomos. <span className="text-primary" data-id="element-49" data-mp="3o2xi1j2chl7f">Principiante → Avanzado.</span>
      </SectionTitle>
      <p className="mt-3 text-center font-semibold text-slate-600" data-id="element-50" data-mp="3k1k9v2bs55xc">
        Más de 440 páginas de hipertrofia, adaptaciones y casos reales listos para implementar.
      </p>

      <div className="mt-8 space-y-3" data-id="element-51" data-mp="n6dkyy6y74zh">
        {modules.map((mod, index) => <div key={mod.name} className={`overflow-hidden rounded-2xl border bg-white transition-colors duration-150 ease-out ${open === index ? 'border-primary shadow-lg shadow-slate-200' : 'border-line'}`} data-id="element-52" data-mp="s3wx9sk357os" data-mpi={index}>
            <button className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left md:px-6" aria-expanded={open === index} onClick={() => setOpen(open === index ? -1 : index)} data-id="element-53" data-mp="1211jtthuf564">
              <span className="flex items-center gap-3" data-id="element-54" data-mp="1fusmqj12jqsv">
                <span className="text-2xl" data-id="element-55" data-mp="1fwdfq47qj1om">{mod.icon}</span>
                <span data-id="element-56" data-mp="1kux1a3if2bgg">
                  <b className="block text-sm font-black uppercase text-ink md:text-base" data-id="element-57" data-mp="1krj191x86ec6">
                    {mod.name}
                  </b>
                  <small className="font-bold text-primary" data-id="element-58" data-mp="3abakintdrmte">{mod.period}</small>
                </span>
              </span>
              <ChevronDownIcon className={`h-5 w-5 shrink-0 text-primary transition-transform duration-200 ease-out ${open === index ? 'rotate-180' : ''}`} data-id="element-59" data-mp="35av75w93mfbf" />
            </button>
            <AnimatePresence initial={false} data-id="element-60" data-mp="2ves32kuos6lu">
              {open === index && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={collapseTransition} data-id="element-61" data-mp="3t07f6ssowhc5">
                  <ul className="space-y-2 border-t border-line px-5 py-4 text-sm font-semibold text-slate-600 md:px-6" data-id="element-62" data-mp="3eccjd3jdxwuu">
                    {mod.content.map(line => <li key={line} className="flex gap-2" data-id="element-63" data-mp="fofel2k2ca0h">
                        <span className="text-primary" data-id="element-64" data-mp="aukxm6vue953">→</span>
                        {line}
                      </li>)}
                  </ul>
                </motion.div>}
            </AnimatePresence>
          </div>)}
      </div>
    </section>;
}
export function BonusesSection() {
  return <section className="border-y border-line bg-white px-4 py-12 md:py-14" data-id="element-65" data-mp="1vbkds1vpuvgq">
      <div className="mx-auto max-w-6xl" data-id="element-66" data-mp="20b6q893q07oh">
        <Eyebrow data-id="element-67" data-mp="1md01bigr1i0y">
          <span className="inline-flex items-center gap-2" data-id="element-68" data-mp="1mcgrkp195zy0">
            <GiftIcon className="h-4 w-4" data-id="element-69" data-mp="1mf30ot3sknic" /> 5 bonos siempre incluidos
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-70" data-mp="1qc8sqgobh3tc">
          Además te llevás <span className="text-primary" data-id="element-71" data-mp="1cflwkg46uhoh">5 bonos gratis</span>
        </SectionTitle>
        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-72" data-mp="1hdnc4r2enyv7">
          {bonuses.map(bonus => <article key={bonus.number} className="flex flex-col rounded-2xl border border-line bg-canvas p-5 shadow-sm" data-id="element-73" data-mp="36sbncoz9farn">
              <div className="flex gap-3" data-id="element-74" data-mp="3bpk8myfpfux4">
                <span className="text-3xl" data-id="element-75" data-mp="103rs7i11hzub">{bonus.icon}</span>
                <div data-id="element-76" data-mp="v5hzrf6p9dce">
                  <small className="font-extrabold uppercase tracking-wide text-primary" data-id="element-77" data-mp="v601usg1mls8">
                    Bono {bonus.number}
                  </small>
                  <h3 className="text-base font-black uppercase leading-tight text-ink" data-id="element-78" data-mp="19wh3absohbp4">
                    {bonus.name}
                  </h3>
                </div>
              </div>
              <p className="mt-4 text-sm font-semibold leading-relaxed text-slate-600" data-id="element-79" data-mp="1ithqwg7xb5ri">
                {bonus.description}
              </p>
              <ul className="my-4 flex-1 space-y-1.5 text-sm font-semibold leading-snug text-slate-600" data-id="element-80" data-mp="1ssyt2nnyv1iq">
                {bonus.items.map(item => <li key={item} className="flex gap-2" data-id="element-81" data-mp="1nuovk6erroty">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary" data-id="element-82" data-mp="3uk3j5fa93u0y" />
                    {item}
                  </li>)}
              </ul>
              <div className="mt-auto flex items-center border-t border-line pt-4 text-sm font-extrabold" data-id="element-83" data-mp="3pnv74arcogy0">
                <span className="text-slate-400 line-through" data-id="element-84" data-mp="38rg0ggx4v8gt">{bonus.value} valor</span>
                <span className="ml-auto rounded-full bg-accent px-3 py-1 text-ink" data-id="element-85" data-mp="38p38daxnvhq1">GRATIS HOY</span>
              </div>
            </article>)}
        </div>
        <p className="mt-7 text-center text-base font-bold text-slate-600 md:text-lg" data-id="element-86" data-mp="3ifxy4pa48zg8">
          Valor total de bonos: <s className="text-slate-400" data-id="element-87" data-mp="2kv1y3hf30iw1">$58.950 ARS</s> →{' '}
          <span className="font-black text-primary" data-id="element-88" data-mp="2prsa9tnrmuc6">incluidos gratis hoy</span>
        </p>
      </div>
    </section>;
}
export function Results() {
  return <section className="mx-auto max-w-5xl px-4 py-12 md:py-14" data-id="element-89" data-mp="2uu16cyvxqcii">
      <Eyebrow data-id="element-90" data-mp="2yu1mqp2lgxs7">❤️ Resultados reales</Eyebrow>
      <SectionTitle data-id="element-91" data-mp="a1fssvbk7y0b">
        Entrenadores que <span className="text-primary" data-id="element-92" data-mp="a0e7ly0txv4v">revolucionaron su negocio</span>
      </SectionTitle>

      <div className="mt-8 space-y-4" data-id="element-93" data-mp="e29erj5fz2qk">
        {developedTestimonials.map(item => <article key={item.name} className="rounded-2xl border border-line bg-white p-5 shadow-sm md:flex md:gap-6 md:p-6" data-id="element-94" data-mp="1yllf7xyjqbif">
            <div className="flex items-center gap-3 md:w-60 md:shrink-0 md:flex-col md:items-start" data-id="element-95" data-mp="1uludiz7heaz4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-primary/20 bg-canvas text-sm font-black text-primary" data-id="element-96" data-mp="1pqf3yjgib18i">
                {item.initials}
              </span>
              <div data-id="element-97" data-mp="1avqyh5b3w2sw">
                <p className="text-sm font-extrabold text-ink" data-id="element-98" data-mp="1av7r2ij30wt4">{item.name}</p>
                <p className="text-xs font-semibold leading-snug text-slate-500" data-id="element-99" data-mp="16tm6sc47xg8w">📍 {item.place}</p>
              </div>
            </div>
            <div className="mt-4 border-t border-line pt-4 md:mt-0 md:flex-1 md:border-l md:border-t-0 md:pl-6 md:pt-0" data-id="element-100" data-mp="11w48rgrqolfo">
              <div className="flex flex-wrap items-center gap-3" data-id="element-101" data-mp="3175bq70kw148">
                <span className="text-sm text-gold" data-id="element-102" data-mp="2w9vhme28v1a2">⭐⭐⭐⭐⭐</span>
                <span className="rounded-full bg-primary/10 px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide text-primary" data-id="element-103" data-mp="2554sdu8pqe5g">
                  {item.result}
                </span>
              </div>
              <p className="mt-3 text-sm font-semibold italic leading-relaxed text-slate-700" data-id="element-104" data-mp="2e6cnk3fxmyvb">
                “{item.text}”
              </p>
            </div>
          </article>)}
      </div>
    </section>;
}
export function Guarantee() {
  return <section className="mx-auto max-w-5xl px-4 pb-12 md:pb-14" data-id="element-105" data-mp="1lf2qtx5699u9">
      <div className="flex flex-col items-center gap-5 rounded-3xl border-2 border-primary/20 bg-white p-6 text-center shadow-sm md:flex-row md:p-8 md:text-left" data-id="element-106" data-mp="1hglkhftks3dw">
        <span className="inline-flex shrink-0 rounded-full border-2 border-primary bg-canvas p-4" data-id="element-107" data-mp="1qgic9qb9hid6">
          <ShieldIcon className="h-9 w-9 text-primary" data-id="element-108" data-mp="12qea7ndq1qoa" />
        </span>
        <div data-id="element-109" data-mp="12okz9vw2skah">
          <h3 className="text-lg font-black uppercase text-ink md:text-xl" data-id="element-110" data-mp="xrkudoaypnky">
            Garantía de 7 días, sin preguntas
          </h3>
          <p className="mt-2 text-sm font-semibold leading-relaxed text-slate-600" data-id="element-111" data-mp="39dve2lapix56">
            Abrís los tomos y los usás con tus alumnos. Si no es lo que esperabas, escribís a
            compradigitalyaa@gmail.com y te devolvemos el 100%. Sin formularios, sin drama. Tasa de
            devoluciones: <b className="text-primary" data-id="element-112" data-mp="35cjmia89w1uc">menos del 0,5%</b>.
          </p>
        </div>
      </div>
    </section>;
}
export function FAQSection() {
  const [open, setOpen] = useState(0);
  return <section className="border-t border-line bg-white px-4 py-12 md:py-14" data-id="element-113" data-mp="zednzqx5r8o">
      <div className="mx-auto max-w-3xl" data-id="element-114" data-mp="3j4zsnjwh0fzh">
        <Eyebrow data-id="element-115" data-mp="3j3y5wi63e51x">
          <span className="inline-flex items-center gap-2" data-id="element-116" data-mp="3j4hp027hazhj">
            <HelpCircleIcon className="h-4 w-4" data-id="element-117" data-mp="3o31a60e9078u" /> Preguntas frecuentes
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-118" data-mp="27oj8xedvvd6t">
          Lo que más <span className="text-primary" data-id="element-119" data-mp="27o9hdmd6wxz0">nos preguntan</span>
        </SectionTitle>
        <div className="mt-8 space-y-3" data-id="element-120" data-mp="2cq8liygr7yx0">
          {faqs.map(([question, answer], index) => <div key={question} className={`overflow-hidden rounded-2xl border bg-white transition-colors duration-150 ease-out ${open === index ? 'border-primary/50 shadow-lg shadow-slate-200' : 'border-line'}`} data-id="element-121" data-mp="1sl9ib0ci4j6j" data-mpi={index}>
              <button className="flex w-full items-center justify-between gap-3 p-5 text-left text-sm font-extrabold text-ink md:text-base" onClick={() => setOpen(open === index ? -1 : index)} aria-expanded={open === index} data-id="element-122" data-mp="1evea44lhe0tv">
                {question}
                <ChevronDownIcon className={`h-5 w-5 shrink-0 text-primary transition-transform duration-200 ease-out ${open === index ? 'rotate-180' : ''}`} data-id="element-123" data-mp="10ulgdm3079rt" />
              </button>
              <AnimatePresence initial={false} data-id="element-124" data-mp="2v8369fqvsiq0">
                {open === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={collapseTransition} className="border-t border-line px-5 py-4 text-sm font-semibold leading-relaxed text-slate-600" data-id="element-125" data-mp="2qa1kgn22m5au">
                    {answer}
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </div>

        <div className="mt-9 rounded-2xl bg-canvas p-6 text-center" data-id="element-126" data-mp="2ewt05tc63u10">
          <p className="text-base font-black text-ink md:text-xl" data-id="element-127" data-mp="2juufq4adxb7q">
            🎯 El próximo ciclo puede ser diferente.
          </p>
          <p className="mt-2 text-sm font-semibold italic text-primary" data-id="element-128" data-mp="2ov8rnxf3x5b1">
            “Finalmente un programa que FUNCIONA.” 💪
          </p>
          <p className="text-sm font-semibold italic text-primary" data-id="element-129" data-mp="43gd28ng8c0f">
            “Finalmente un alumno que PROGRESA porque tiene un sistema.”
          </p>
          <p className="text-sm font-semibold italic text-primary" data-id="element-130" data-mp="86le06rvs5my">
            “Finalmente una comunidad de entrenadores que SABE qué hacer.”
          </p>
          <div className="mt-5" data-id="element-131" data-mp="1pbi30nb3cdj">
            <Button href={purchaseUrl} className="w-full text-sm md:w-auto md:text-base" data-id="element-132" data-mp="1q317bzpvhmx">
              💪 QUIERO MI COMBO HIPERTROFIA AHORA →
            </Button>
          </div>
          <p className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] font-semibold text-slate-500" data-id="element-133" data-mp="blmya1q0omd9">
            <span className="flex items-center gap-1" data-id="element-134" data-mp="pk4i3ocsxepw">
              <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-135" data-mp="km2zf4jafhey" /> Garantía 7 días
            </span>
            <span className="flex items-center gap-1" data-id="element-136" data-mp="zaz3kqi84aaa">
              <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-137" data-mp="ucxjc292a4x8" /> Acceso en 5 minutos
            </span>
            <span className="flex items-center gap-1" data-id="element-138" data-mp="18cou7u4a7570">
              <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-139" data-mp="18d8bd8m3xpjz" /> Pago seguro
            </span>
          </p>
        </div>
      </div>
    </section>;
}